import { FC } from 'react'
import { ColumnDef } from '@atbay/cyber-components'
import { CustomTooltip } from '@atbay/component-lib'
import AssetIcon from '@/ui/components/icons/asset-icon'
import { useCopyWithFeedback } from '@/ui/components/caseNumberDisplay/useCopyWithFeedback'
import Typography from '@/ui/components/typography'
import { ASSET_TYPE_LABELS } from '@/core/modules/digital-footprint/assets/constants'
import { TSelectableAsset } from '@ui/drawer/common/useIssueResolution'
import CopyIcon from '@/assets/images/copy-icon.svg?react'
import {
    AssetNameCell,
    CopyIconWrapper,
    PathRow,
    StackedCell,
} from './affectedAssetsColumns.styles'

const columnHeader = (label: string) => (
    <Typography variant="s-regular">{label}</Typography>
)

export const assetNameColumn = (): ColumnDef<TSelectableAsset> =>
    ({
        id: 'hostname',
        header: () => columnHeader('Asset Name'),
        cell: ({ row }) => (
            <AssetNameCell data-testid="affected-asset-name">
                <AssetIcon
                    asset_type={row.original.asset.asset_type as TAssetType}
                />
                <Typography variant="s-regular" noWrap={false}>
                    {row.original.hostname}
                </Typography>
            </AssetNameCell>
        ),
        enableSorting: false,
        width: '38%',
    }) as ColumnDef<TSelectableAsset>

const getLoginUrls = (asset: TSelectableAsset['asset']): string[] =>
    (
        (asset as Partial<TUserLeakedCredIssueAsset>)
            .leaked_credentials_data ?? []
    ).map(({ login_url }) => login_url)

export const loginUrlsColumn = (): ColumnDef<TSelectableAsset> => ({
    id: 'login_urls',
    header: () => columnHeader('Login URL'),
    cell: ({ row }) => (
        <StackedCell data-testid="affected-asset-login-urls">
            {getLoginUrls(row.original.asset).map((url) => (
                <Typography key={url} variant="s-regular" noWrap={false}>
                    {url}
                </Typography>
            ))}
        </StackedCell>
    ),
    enableSorting: false,
})

export const classificationColumn = (): ColumnDef<TSelectableAsset> => ({
    id: 'classification',
    header: () => columnHeader('Classification'),
    cell: ({ row }) => (
        <Typography
            variant="s-regular"
            testId="affected-asset-classification"
        >
            {row.original.asset.asset_type
                ? ASSET_TYPE_LABELS[row.original.asset.asset_type]
                : ''}
        </Typography>
    ),
    enableSorting: false,
})

export const ipColumn = (): ColumnDef<TSelectableAsset> =>
    ({
        id: 'ip',
        header: () => columnHeader('IP'),
        cell: ({ row }) => (
            <Typography variant="s-regular" testId="affected-asset-ip">
                {(
                    (row.original.asset as Partial<TDevice>).ip_addresses ?? []
                )
                    .map(({ ip_address }) => ip_address)
                    .join(', ')}
            </Typography>
        ),
        enableSorting: false,
        width: '24%',
    }) as ColumnDef<TSelectableAsset>

type TInfostealerAsset = Partial<TDeviceInfostealerIssueAsset> & {
    malware_path?: string
}

const getMalwarePaths = (asset: TSelectableAsset['asset']): string[] => {
    const infostealerAsset = asset as TInfostealerAsset
    return (
        infostealerAsset.device_infostealer_data?.malware_paths ??
        (infostealerAsset.malware_path ? [infostealerAsset.malware_path] : [])
    )
}

const CopyablePath: FC<{ path: string }> = ({ path }) => {
    const [copied, copyToClipboard] = useCopyWithFeedback()

    return (
        <CustomTooltip
            title={copied ? 'Copied!' : path}
            placement="top"
            alwaysShowOnHover
        >
            <PathRow>
                <Typography variant="s-regular" component="span">
                    {path}
                </Typography>
                <CopyIconWrapper
                    data-testid="copy-malware-path"
                    onClick={() => copyToClipboard(path)}
                >
                    <CopyIcon width={13} height={15} role="img" />
                </CopyIconWrapper>
            </PathRow>
        </CustomTooltip>
    )
}

export const malwarePathsColumn = (): ColumnDef<TSelectableAsset> =>
    ({
        id: 'malware_paths',
        header: () => columnHeader('Malware Path'),
        cell: ({ row }) => (
            <StackedCell data-testid="affected-asset-malware-paths">
                {getMalwarePaths(row.original.asset).map((path) => (
                    <CopyablePath key={path} path={path} />
                ))}
            </StackedCell>
        ),
        enableSorting: false,
        width: '38%',
    }) as ColumnDef<TSelectableAsset>

export const hasIpData = (rows: TSelectableAsset[]): boolean =>
    rows.some(
        ({ asset }) =>
            ((asset as Partial<TDevice>).ip_addresses ?? []).length > 0
    )

export const hasMalwarePathData = (rows: TSelectableAsset[]): boolean =>
    rows.some(({ asset }) => getMalwarePaths(asset).length > 0)
